/*
 * Copyright (c) 2019. Sonrai Analytics Ltd.
 * Developed by Deva Sen. & Warren Dowey
 */

import { Injectable } from '@angular/core';
import { ApiBaseService } from '@app/services/api-services/api-base.service';
import { AppsListService } from '@app/services/api-services/apps/apps-list.service';
import { AppModel } from '@app/models/app.model';
import { ApiParamsModel } from '@app/models/api-params.model';
import { ApiFilterModel } from '@app/models/api-filter.model';
import { HttpClient } from '@angular/common/http';
import { AuthUserService } from '@app/services/authentication/auth-user-service';
@Injectable({
  providedIn: 'root',
})
export class AppService extends ApiBaseService {
  private _app: AppModel;
  protected _endpoint = 'apps';

  constructor(
    private appsListService: AppsListService,
    http: HttpClient,

    authUserService: AuthUserService
  ) {
    super(http, authUserService);
  }

  get app(): AppModel {
    return this._app;
  }

  set app(value: AppModel) {
    this._app = value;
  }

  public getAppFromListById(appId: string): void {
    this.app = this.appsListService.getAppById(appId);
    if (this.app) {
      this.dataSubject.next(this.app);
    } else {
      this.getAppById(appId);
    }
  }

  public getAppById(appId: string): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = { field: 'app_id', operator: 'eq', value: appId };
    apiParamsModel.filter = [apiFilterModel];
    this.getData(apiParamsModel);
  }
  /**
   *  Warren Dowey
   *
   *  getAppFromListBySlug
   *
   * : Get an app from the list based on the slug
   *
   * @param slug of type string
   *
   * @returns Void: gets the slug that meets the query
   */
  public getAppFromListBySlug(slug: string): void {
    this.app = this.appsListService.getAppBySlug(slug);
    if (this.app) {
      this.dataSubject.next(this.app);
    } else {
      this.getAppBySlug(slug);
    }
  }
  /**
   *  Warren Dowey
   *
   *  getAppBySlug
   *
   * : Get an app based on the slug using an API query
   *
   * @param slug of type string
   *
   * @returns Void: gets the slug that meets the query
   */
  public getAppBySlug(slug: string): void {
    const apiParamsModel = new ApiParamsModel();
    const apiFilterModel: ApiFilterModel = { field: 'app_slug', operator: 'eq', value: slug };
    apiParamsModel.filter = [apiFilterModel];
    this.getData(apiParamsModel);
  }
  /**
   *  Warren Dowey
   *
   *  handleData
   *
   * : Handle the data from the app using the API base service
   *
   * @param data of type AppModel
   *
   * @returns Void: handles the APImodel data
   */
  protected handleData(data: AppModel[]): void {
    this.app = data[0];
    super.handleData(data);
  }
  /**
   *  Warren Dowey
   *
   *  handleInsert
   *
   * : Handle the data from the app using the API base service
   *
   * @param data of type AppModel
   *
   * @returns Void: handles the APImodel data
   */
  protected handleInsert(data: AppModel[]): void {
    this.app = data[0];
    this.appsListService.addToList(data[0]);
    super.handleInsert(data);
  }
  /**
   *  Warren Dowey
   *
   *  handleUpdate
   *
   * : Handle the data update to the app list using the API base service
   *
   * @param data of type AppModel
   *
   * @returns Void: handles the APImodel data
   */
  protected handleUpdate(data: AppModel[]): void {
    this.app = data[0];
    this.appsListService.updateInList(data[0]);
    super.handleUpdate(data);
  }
  /**
   *  Warren Dowey
   *
   *  handleDelete
   *
   * : Handle the delete from the app listusing the API base service
   *
   * @param data of type AppModel
   *
   * @returns Void: handles the APImodel data
   */
  protected handleDelete(data: AppModel[]): void {
    this.appsListService.removeFromList(data[0]);
    super.handleDelete(data);
  }
}
